import type { ReactNode } from 'react'
import { ClockIcon, CoinsIcon, Base } from '@itsrever/design-system'
import { RefundSummary } from './process-island/summary-tab/refund-summary'
import { getDateFromDate } from '@/utils'
import { Trans, useTranslation } from 'react-i18next'
import { ReturnProcess } from '@/domain/returns'
import { Compensation } from '@/domain/refund'
import { PbReturnSummaryRefundTimingEnum } from '@itsrever/rever-api'

const { Card, InfoTooltip } = Base

interface SummaryProps {
  processDetails: ReturnProcess | undefined
  children?: ReactNode
}

export const SummaryIsland: React.FC<SummaryProps> = ({
  processDetails,
  children
}) => {
  const { t, i18n } = useTranslation()

  const returnedItems = processDetails?.returnedItems ?? []
  const exchangeItems = processDetails?.orderedItems ?? []
  const extraLineItems = processDetails?.extraLineItems ?? []

  const compensation = processDetails?.compensation ?? ({} as Compensation)

  const userSelectedPM =
    processDetails?.refundPaymentMethod ?? 'NO_REFUND_METHOD'

  const isMarkedAsARefunded =
    processDetails?.compensation.statusSummary?.externalRefundDetails

  const isPendingRefund =
    parseFloat(
      processDetails?.compensation?.statusSummary?.pendingRefundAmount
        ?.amount ?? '0'
    ) > 0

  return (
    <Card className="h-fit p-4">
      {!processDetails?.isOnlyExchange &&
      (processDetails?.commitedAmount ?? 0) > 0 ? (
        <div className="pb-3">
          <div>
            {!isPendingRefund ? (
              <div className="rounded-md bg-green-100 p-3">
                <div className="flex justify-start gap-1.5">
                  <CoinsIcon className="mt-0.5 size-4 shrink-0 text-neutral-700" />
                  <div>
                    <p className="flex flex-wrap items-center gap-x-1.5">
                      <span className="text-sm font-semibold text-neutral-700">
                        {t('process_details_page.refunded')}
                      </span>
                      {processDetails?.firstRefundDate ? (
                        <span className="text-xs font-light text-green-700">
                          {getDateFromDate(
                            new Date(processDetails?.firstRefundDate ?? ''),
                            i18n.language
                          )}
                        </span>
                      ) : null}
                    </p>
                    {isMarkedAsARefunded ? (
                      <div className="flex items-center gap-1 pt-0.5">
                        <p className="text-xs font-normal text-green-700">
                          {processDetails?.compensation.statusSummary
                            ?.externalRefundDetails?.user ? (
                            <Trans i18nKey="process_details_page.marked_as_refunded_by" />
                          ) : (
                            <Trans i18nKey="process_details_page.marked_as_refunded_externally" />
                          )}
                        </p>
                        <span className="flex items-center gap-1 font-roobert text-xs font-medium text-green-900">
                          {
                            processDetails?.compensation.statusSummary
                              ?.externalRefundDetails?.user
                          }{' '}
                          <InfoTooltip
                            label={t(
                              'process_details_page.external_refund_note'
                            )}
                            content={
                              processDetails?.compensation.statusSummary
                                ?.externalRefundDetails?.note
                            }
                          />
                        </span>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-2 rounded-md bg-yellow-100 px-3 py-2.5">
                <ClockIcon className="size-4 h-[1lh] shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-neutral-700">
                    {t('process_details_page.pending_refund')}
                  </p>
                  {processDetails?.refundTiming !==
                    PbReturnSummaryRefundTimingEnum.NoTiming &&
                  processDetails?.refundTiming !==
                    PbReturnSummaryRefundTimingEnum.Unspecified ? (
                    <p className="pt-0.5 text-xs font-light text-neutral-600 [text-wrap:pretty]">
                      {t(
                        `process_details_page.refund_timing_${processDetails?.refundTiming}`
                      )}
                    </p>
                  ) : null}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : null}
      <RefundSummary
        returnedItems={returnedItems}
        exchangeItems={exchangeItems}
        extraLineItems={extraLineItems}
        compensation={compensation}
        userSelectedPM={userSelectedPM}
      >
        {children}
      </RefundSummary>
    </Card>
  )
}
